package org.zq.astrock_ai.network

import org.zq.astrock_ai.api.dto.DepthLevelDto
import org.zq.astrock_ai.api.dto.IndexQuoteDto
import org.zq.astrock_ai.api.dto.KlineBarDto
import org.zq.astrock_ai.api.dto.MarketCenterResponseDto
import org.zq.astrock_ai.api.dto.MarketOverviewDto
import org.zq.astrock_ai.api.dto.RankedStockDto
import org.zq.astrock_ai.api.dto.SectorQuoteDto
import org.zq.astrock_ai.api.dto.StockQuoteDto
import org.zq.astrock_ai.data.DepthLevel
import org.zq.astrock_ai.data.IndexQuote
import org.zq.astrock_ai.data.MarketOverview
import org.zq.astrock_ai.data.MetricColor
import org.zq.astrock_ai.data.MetricItem
import org.zq.astrock_ai.data.MockStockData
import org.zq.astrock_ai.data.RankedStock
import org.zq.astrock_ai.data.SectorQuote
import org.zq.astrock_ai.data.StockProfile
import org.zq.astrock_ai.data.WatchlistItem

data class MarketCenterData(
    val overview: MarketOverview,
    val indices: List<IndexQuote>,
    val sectors: List<SectorQuote>,
    val gainers: List<RankedStock>,
    val losers: List<RankedStock>,
    val turnoverLeaders: List<RankedStock>,
)

fun IndexQuoteDto.toUi() = IndexQuote(name, price, changePercent, isUp)

fun MarketOverviewDto.toUi() = MarketOverview(upCount, downCount, flatCount, turnover, northboundFlow)

fun SectorQuoteDto.toUi() = SectorQuote(name, changePercent, isUp, leadingStock)

fun RankedStockDto.toUi() = RankedStock(rank, name, code, price, changePercent, turnover, isUp)

fun DepthLevelDto.toUi() = DepthLevel(label, price, volume, barWidth, isAsk)

fun MarketCenterResponseDto.toUi() = MarketCenterData(
    overview = overview.toUi(),
    indices = indices.map { it.toUi() },
    sectors = sectors.map { it.toUi() },
    gainers = gainers.map { it.toUi() },
    losers = losers.map { it.toUi() },
    turnoverLeaders = turnoverLeaders.map { it.toUi() },
)

fun StockQuoteDto.toStockProfile(): StockProfile {
    val mock = MockStockData.profileByCode(code)
    val ask = askLevels.map { it.toUi() }.ifEmpty { mock?.askLevels.orEmpty() }
    val bid = bidLevels.map { it.toUi() }.ifEmpty { mock?.bidLevels.orEmpty() }
    return StockProfile(
        name = name,
        code = code,
        fullCode = fullCode,
        initial = mock?.initial ?: name.take(1),
        avatarColor = mock?.avatarColor ?: avatarColorFor(name),
        price = price,
        changePercent = changePercent,
        changeAmount = changeAmount,
        isUp = isUp,
        industry = mock?.industry ?: "—",
        marketCap = mock?.marketCap ?: "—",
        openPrice = openPrice,
        metrics = buildMetrics(this, mock?.metrics),
        askLevels = ask,
        bidLevels = bid,
    )
}

fun StockQuoteDto.toWatchlistItem() = WatchlistItem(name, code, price, changePercent, isUp)

fun List<KlineBarDto>.toClosePrices(): List<Float> = map { it.close }

private fun buildMetrics(quote: StockQuoteDto, fallback: List<MetricItem>?): List<MetricItem> {
    if (fallback != null && quote.askLevels.isEmpty()) return fallback
    return listOf(
        MetricItem("今开", quote.openPrice, "昨收 —"),
        MetricItem("最高", quote.highPrice, "振幅 —", if (quote.isUp) MetricColor.Green else MetricColor.Body),
        MetricItem("最低", quote.lowPrice, "换手率 —", MetricColor.Red),
        MetricItem("成交量", quote.volume, "成交额 ${quote.turnover}"),
    )
}

private fun avatarColorFor(name: String): Long {
    val palette = listOf(0xFFB91C1C, 0xFF2563EB, 0xFF059669, 0xFFD97706, 0xFF7C3AED, 0xFF0891B2)
    return palette[name.hashCode().mod(palette.size).let { if (it < 0) it + palette.size else it }]
}
